import { useState } from "react";
import AgentLayout from "@/components/layout/AgentLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Filter, Download, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { apiRequest } from "@/lib/api";
import { Helmet } from "react-helmet";
import { format } from "date-fns";
import { useAuth } from "@/hooks/use-auth";

export default function AgentBookingsPage() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("upcoming");

  // Fetch bookings data
  const { data: bookings, isLoading, isError } = useQuery({
    queryKey: ["agent/bookings"],
    queryFn: getQueryFn(),
    refetchOnWindowFocus: false,
  });

  // Format bookings by status if data is available
  const formattedBookings = bookings
    ? {
        upcoming: bookings.filter((b: any) => ["pending", "confirmed"].includes(b.status)),
        completed: bookings.filter((b: any) => b.status === "completed"),
        cancelled: bookings.filter((b: any) => b.status === "cancelled"),
      }
    : { upcoming: [], completed: [], cancelled: [] };

  // Filter bookings based on active tab, search term, and status filter
  const getFilteredBookings = () => {
    let filtered = [];
    
    if (activeTab === "all" && bookings) {
      filtered = [...bookings];
    } else if (formattedBookings[activeTab as keyof typeof formattedBookings]) {
      filtered = [...formattedBookings[activeTab as keyof typeof formattedBookings]];
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((booking: any) => 
        (booking.property?.title || booking.propertyTitle || "").toLowerCase().includes(term) ||
        (booking.guest?.name || booking.guestName || "").toLowerCase().includes(term) ||
        (booking.guest?.email || booking.guestEmail || "").toLowerCase().includes(term)
      );
    }
    
    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((booking: any) => booking.status === statusFilter);
    }
    
    return filtered;
  };

  const filteredBookings = getFilteredBookings();

  // Helper to get status badge with appropriate styling
  const getStatusBadge = (status: string) => {
    let variant: "default" | "secondary" | "outline" | "destructive" | undefined;
    
    switch(status) {
      case "pending":
        variant = "secondary";
        break;
      case "confirmed":
        variant = "default";
        break;
      case "completed":
        variant = undefined; // Default blue
        break;
      case "cancelled":
        variant = "destructive";
        break;
      default:
        variant = "outline";
    }
    
    return (
      <Badge variant={variant}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <AgentLayout>
      <Helmet>
        <title>My Bookings | StayMiddle</title>
      </Helmet>
      <div className="container py-10">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-1">My Bookings</h1>
            <p className="text-muted-foreground">View bookings from your property links</p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-4">
            <Button variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              <span>Export</span>
            </Button>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search by property, guest or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="upcoming">Upcoming ({(formattedBookings?.upcoming || []).length})</TabsTrigger>
            <TabsTrigger value="completed">Completed ({(formattedBookings?.completed || []).length})</TabsTrigger>
            <TabsTrigger value="cancelled">Cancelled ({(formattedBookings?.cancelled || []).length})</TabsTrigger>
            <TabsTrigger value="all">All Bookings ({(bookings || []).length})</TabsTrigger>
          </TabsList>
        </Tabs>

        <Card>
          <CardHeader className="px-6">
            <CardTitle>Booking Details</CardTitle>
            <CardDescription>View all bookings made through your agent links</CardDescription>
          </CardHeader>
          <CardContent className="px-6">
            {isLoading ? (
              <div className="flex justify-center items-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredBookings.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Check-in</TableHead>
                    <TableHead>Check-out</TableHead>
                    <TableHead>Guests</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.map((booking: any) => (
                    <TableRow key={booking.id}>
                      <TableCell className="font-medium">
                        {booking.property?.title || booking.propertyTitle || `Property #${booking.propertyId}`}
                      </TableCell>
                      <TableCell>
                        <div>{booking.guest?.name || booking.guestName || "Guest"}</div>
                        <div className="text-xs text-muted-foreground">{booking.guest?.email || booking.guestEmail}</div>
                      </TableCell>
                      <TableCell>{format(new Date(booking.checkIn), 'MMM d, yyyy')}</TableCell>
                      <TableCell>{format(new Date(booking.checkOut), 'MMM d, yyyy')}</TableCell>
                      <TableCell>{booking.guestCount}</TableCell>
                      <TableCell>
                        {booking.totalAmount ? 
                          new Intl.NumberFormat('en-AE', {
                            style: 'currency',
                            currency: 'AED',
                            minimumFractionDigits: 0,
                          }).format(booking.totalAmount) : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        {booking.markupAmount ? 
                          new Intl.NumberFormat('en-AE', {
                            style: 'currency',
                            currency: 'AED',
                            minimumFractionDigits: 0,
                          }).format(booking.markupAmount) : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell>{getStatusBadge(booking.status)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="h-8">
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No bookings found matching your filters.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AgentLayout>
  );
}